import isEqual from 'lodash/isEqual';
import {
  DesignerSourceFile,
  sourceFileKey,
} from '@packages/domains/code-libraries/data-types';
import {ensureLeadingSlash, stripLeadingSlash} from '../paths';

export type SourceFilesState = Record<string, DesignerSourceFile>;

export const initialSourceFilesState: SourceFilesState = {};

export const isInstructionKind = (kind: DesignerSourceFile['kind']): boolean =>
  kind === 'rule' || kind === 'skill';

export const isSameInstructionDocument = (
  existing: Pick<DesignerSourceFile, 'id' | 'kind'>,
  incoming: Pick<DesignerSourceFile, 'id' | 'kind'>
): boolean =>
  isInstructionKind(existing.kind) &&
  isInstructionKind(incoming.kind) &&
  existing.id !== undefined &&
  incoming.id !== undefined &&
  existing.id === incoming.id;

// MPS misses REST instruction writes, so rolling v5 back to v3 would make the
// next autosave 409. Versions are document-local, so this protection only
// applies when both records identify the same instruction document.
const shouldKeepExistingInstruction = (
  existing: DesignerSourceFile,
  incoming: DesignerSourceFile
): boolean => {
  if (!isSameInstructionDocument(existing, incoming)) return false;
  if (existing.version === undefined) return false;
  return incoming.version === undefined || existing.version > incoming.version;
};

// Initial hydration stays authoritative for additions and deletions.
export const mergeWholesaleSourceFiles = (
  existing: SourceFilesState,
  incoming: DesignerSourceFile[]
): SourceFilesState => {
  const next: SourceFilesState = {};
  for (const file of incoming) {
    const parsed = DesignerSourceFile.parse(file);
    const key = sourceFileKey(parsed);
    const prior = existing[key];
    next[key] =
      prior && shouldKeepExistingInstruction(prior, parsed) ? prior : parsed;
  }
  return next;
};

// MPS catchup replaces code files but cannot change REST-owned instructions.
export const mergeCatchupSourceFiles = (
  existing: SourceFilesState,
  incoming: DesignerSourceFile[]
): SourceFilesState => {
  const next: SourceFilesState = {};
  for (const [key, file] of Object.entries(existing)) {
    if (isInstructionKind(file.kind)) {
      next[key] = file;
    }
  }
  for (const file of incoming) {
    const parsed = DesignerSourceFile.parse(file);
    if (!isInstructionKind(parsed.kind)) {
      next[sourceFileKey(parsed)] = parsed;
    }
  }
  return next;
};

// Compare values so refetches can reuse the existing store reference.
export const designerSourceFilesEqual = (
  a: DesignerSourceFile,
  b: DesignerSourceFile
): boolean =>
  a.folderId === b.folderId &&
  a.name === b.name &&
  a.path === b.path &&
  a.content === b.content &&
  a.kind === b.kind &&
  a.isDraft === b.isDraft &&
  a.publishedOn?.getTime() === b.publishedOn?.getTime() &&
  a.id === b.id &&
  a.version === b.version &&
  a.description === b.description &&
  a.createdAt?.getTime() === b.createdAt?.getTime() &&
  a.updatedAt?.getTime() === b.updatedAt?.getTime() &&
  isEqual(a.metadata, b.metadata) &&
  a.origin?.[0] === b.origin?.[0] &&
  a.origin?.[1] === b.origin?.[1];

/**
 * Canonical mapping from a source file to its sandbox filesystem path.
 *
 * Prefers the persisted `path` (full folder path) when present, falling back to `<folderId>/<name>` for files created
 * before `path` was populated. This is one half of an inverse pair: `sandboxPathIndex` must map each sandbox path back
 * to the file that projected there.
 */
export const sourceFileSandboxPath = (file: DesignerSourceFile): string => {
  if (file.path) {
    return ensureLeadingSlash(file.path);
  }
  if (!file.folderId) {
    return ensureLeadingSlash(file.name);
  }
  return ensureLeadingSlash(`${file.folderId}/${stripLeadingSlash(file.name)}`);
};

/**
 * Reverse of `sourceFileSandboxPath`: an index from each file's sandbox path (without leading slash) to its
 * `SourceFilesState` key. Paths absent from the index belong to genuinely new files. If two files collide on the same
 * sandbox path (e.g. a duplicate minted before `path` was persisted), the file with a persisted `path` wins.
 */
export const sandboxPathIndex = (
  state: SourceFilesState
): ReadonlyMap<string, string> => {
  const index = new Map<string, string>();
  for (const [key, file] of Object.entries(state)) {
    const path = stripLeadingSlash(sourceFileSandboxPath(file));
    if (!index.has(path) || file.path) {
      index.set(path, key);
    }
  }
  return index;
};
